import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useDepots, fmtDepot } from "@/lib/depots";
import {
  UserCircle, IdentificationCard, Plus, Pencil, Trash, MagnifyingGlass,
  X, Phone, Buildings, CalendarBlank, ArrowsCounterClockwise, UploadSimple, CheckCircle, Warning,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const DEPOTS = ["Panaji", "Margao", "Vasco", "Mapusa", "Ponda", "Canacona"];
const SHIFT_STATUSES = ["On Duty", "Off Duty", "On Break"];

const shiftBadge = (s) => ({
  "On Duty":  "bg-green-50 text-green-700 border-green-200",
  "Off Duty": "bg-slate-100 text-slate-600 border-slate-200",
  "On Break": "bg-amber-50 text-amber-700 border-amber-200",
}[s] || "bg-slate-100 text-slate-600");

export default function CrewManagement() {
  const depotMap = useDepots();
  const [drivers, setDrivers] = useState([]);
  const [conductors, setConductors] = useState([]);
  const [tab, setTab] = useState("drivers");
  const [q, setQ] = useState("");
  const [depotFilter, setDepotFilter] = useState("All");
  const [shiftFilter, setShiftFilter] = useState("All");
  const [edit, setEdit] = useState(null); // { kind, doc } | "new"
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [d, c] = await Promise.all([api.get("/crew/drivers"), api.get("/crew/conductors")]);
      setDrivers(d.data); setConductors(c.data);
    } catch { toast.error("Failed to load crew"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const onDuty = (list) => list.filter(x => x.shift_status === "On Duty").length;

  const filtered = useMemo(() => {
    const src = tab === "drivers" ? drivers : conductors;
    return src.filter(x => {
      const matchesText = !q ||
        x.name?.toLowerCase().includes(q.toLowerCase()) ||
        x.employee_id?.toLowerCase().includes(q.toLowerCase()) ||
        x.phone?.includes(q) ||
        (x.license_number || "").toLowerCase().includes(q.toLowerCase());
      const matchesDepot = depotFilter === "All" || x.depot === depotFilter;
      const matchesShift = shiftFilter === "All" || x.shift_status === shiftFilter;
      return matchesText && matchesDepot && matchesShift;
    });
  }, [tab, drivers, conductors, q, depotFilter, shiftFilter]);

  const toggleShift = async (kind, doc) => {
    const next = doc.shift_status === "On Duty" ? "Off Duty" : "On Duty";
    try {
      await api.patch(`/crew/${kind}s/${doc.id}`, { shift_status: next });
      toast.success(`${doc.name} marked ${next}`);
      load();
    } catch { toast.error("Failed to update shift"); }
  };

  const remove = async (kind, doc) => {
    if (!window.confirm(`Delete ${kind} "${doc.name}" (${doc.employee_id})?\n\nThis cannot be undone.`)) return;
    try {
      await api.delete(`/crew/${kind}s/${doc.id}`);
      toast.success(`${doc.name} removed`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const downloadTemplate = () => {
    const isDriver = tab === "drivers";
    const header = isDriver
      ? "employee_id,name,license_number,license_expiry,depot,phone,duty_hours,shift_status,violations"
      : "employee_id,name,depot,phone,duty_hours,shift_status";
    const sample = isDriver
      ? "DRV900,Sample Driver,GA-12345678,2027-12-31,Panaji,+919999988888,0,Off Duty,0"
      : "CON900,Sample Conductor,Panaji,+919999988888,0,Off Duty";
    const csv = `${header}\n${sample}`;
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = `${tab}-template.csv`; a.click();
  };

  const bulkUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const text = await file.text();
    const [header, ...rest] = text.trim().split(/\r?\n/);
    const cols = header.split(",").map(c => c.trim());
    const rows = rest.map(r => { const v = r.split(","); return Object.fromEntries(cols.map((c, i) => [c, (v[i] || "").trim()])); });
    try {
      const { data } = await api.post("/crew/bulk", { kind: tab === "drivers" ? "driver" : "conductor", rows });
      toast.success(`Created ${data.created} · skipped ${data.skipped.length}`);
      load();
    } catch { toast.error("Bulk upload failed"); }
    e.target.value = "";
  };

  return (
    <div className="p-4 lg:p-6 space-y-5 lg:space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="label-cap text-slate-500">Workforce</div>
          <h1 className="font-display text-2xl lg:text-3xl font-bold leading-tight">Crew Management</h1>
          <div className="text-xs text-slate-500 mt-1">Manage on-board drivers and conductors · {drivers.length + conductors.length} total</div>
        </div>
        <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
          <button onClick={load} title="Refresh"
                  className="px-3 py-2 border border-slate-200 text-slate-600 rounded-md text-xs hover:bg-slate-50 flex items-center gap-1.5">
            <ArrowsCounterClockwise size={14}/><span className="hidden sm:inline">Refresh</span>
          </button>
          <button onClick={downloadTemplate}
                  className="px-3 py-2 border border-slate-200 rounded-md text-xs hover:bg-slate-50 whitespace-nowrap">
            <span className="hidden sm:inline">CSV </span>Template
          </button>
          <label className="px-3 py-2 border border-slate-200 rounded-md text-xs cursor-pointer hover:bg-slate-50 flex items-center gap-1.5 whitespace-nowrap">
            <UploadSimple size={14}/><span className="hidden sm:inline">Bulk </span>CSV
            <input type="file" accept=".csv" className="hidden" onChange={bulkUpload}/>
          </label>
          <button data-testid="crew-add-btn" onClick={() => setEdit({ kind: tab === "drivers" ? "driver" : "conductor", doc: null })}
                  className="ktcl-btn-primary px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-semibold flex items-center gap-1.5 whitespace-nowrap">
            <Plus size={14}/>
            <span className="hidden sm:inline">Add {tab === "drivers" ? "Driver" : "Conductor"}</span>
            <span className="sm:hidden">Add</span>
          </button>
        </div>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { l: "Total Drivers",      v: drivers.length,    c: "#1E3A8A", i: UserCircle },
          { l: "Drivers On Duty",    v: onDuty(drivers),   c: "#22C55E", i: UserCircle },
          { l: "Total Conductors",   v: conductors.length, c: "#1E3A8A", i: IdentificationCard },
          { l: "Conductors On Duty", v: onDuty(conductors),c: "#22C55E", i: IdentificationCard },
        ].map(x => (
          <div key={x.l} className="ktcl-card p-4">
            <div className="flex items-center justify-between">
              <div className="label-cap text-slate-500">{x.l}</div>
              <x.i size={18} weight="duotone" style={{ color: x.c }}/>
            </div>
            <div className="font-display text-2xl font-bold mt-2" style={{ color: x.c }}>{x.v}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="ktcl-card">
        <div className="flex border-b border-slate-200 overflow-x-auto scroll-thin">
          {[
            { k: "drivers",    l: `Drivers (${drivers.length})` },
            { k: "conductors", l: `Conductors (${conductors.length})` },
          ].map(t => (
            <button key={t.k} onClick={() => setTab(t.k)}
              data-testid={`crew-tab-${t.k}`}
              className={`shrink-0 px-4 sm:px-5 py-3 text-sm whitespace-nowrap ${tab === t.k ? "border-b-2 border-[#1E3A8A] text-[#1E3A8A] font-semibold" : "text-slate-500"}`}>
              {t.l}
            </button>
          ))}
        </div>

        <div className="p-3 lg:p-4 flex items-center gap-2 flex-wrap border-b border-slate-100">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 rounded-md flex-1 min-w-[200px]">
            <MagnifyingGlass size={14} className="text-slate-400"/>
            <input data-testid="crew-search" value={q} onChange={e => setQ(e.target.value)}
                   placeholder="Search by name, ID, phone, license…"
                   className="bg-transparent outline-none text-sm flex-1 min-w-0"/>
          </div>
          <select value={depotFilter} onChange={e => setDepotFilter(e.target.value)}
                  data-testid="crew-depot-filter"
                  className="px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-md text-sm outline-none">
            <option value="All">All Depots</option>
            {DEPOTS.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <select value={shiftFilter} onChange={e => setShiftFilter(e.target.value)}
                  data-testid="crew-shift-filter"
                  className="px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-md text-sm outline-none">
            <option value="All">All Shifts</option>
            {SHIFT_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <span className="text-xs text-slate-500">Showing {filtered.length}</span>
        </div>

        {/* Mobile card list */}
        <div className="lg:hidden divide-y divide-slate-100">
          {loading && <div className="p-8 text-center text-sm text-slate-400">Loading…</div>}
          {!loading && filtered.length === 0 && <EmptyState onAdd={() => setEdit({ kind: tab === "drivers" ? "driver" : "conductor", doc: null })} kind={tab}/>}
          {filtered.map(c => {
            const kind = tab === "drivers" ? "driver" : "conductor";
            return (
              <div key={c.id} className="p-3" data-testid={`crew-row-${c.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm truncate">{c.name}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded border ${shiftBadge(c.shift_status)} whitespace-nowrap`}>{c.shift_status}</span>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                      <span className="font-mono">{c.employee_id}</span>
                      <span>·</span>
                      <span>{fmtDepot(c.depot, depotMap)}</span>
                      {tab === "drivers" && c.license_number && <><span>·</span><span className="font-mono">{c.license_number}</span></>}
                    </div>
                    <div className="flex items-center gap-3 mt-1.5 text-xs">
                      <a href={`tel:${c.phone}`} className="text-[#1E3A8A] hover:underline inline-flex items-center gap-1">
                        <Phone size={12} weight="fill"/>{c.phone}
                      </a>
                      <span className="text-slate-500">⏱ {c.duty_hours}h</span>
                      {tab === "drivers" && (
                        <span className={c.violations > 0 ? "text-red-600 font-semibold" : "text-green-700"}>
                          {c.violations > 0 ? `⚠ ${c.violations} violations` : "✓ Clean"}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    <button onClick={() => toggleShift(kind, c)}
                            className={`px-2 py-1 text-[10px] rounded border ${c.shift_status === "On Duty" ? "border-amber-200 text-amber-700 hover:bg-amber-50" : "border-green-200 text-green-700 hover:bg-green-50"}`}>
                      {c.shift_status === "On Duty" ? "Off Duty" : "On Duty"}
                    </button>
                    <div className="flex gap-1">
                      <button onClick={() => setEdit({ kind, doc: c })}
                              data-testid={`crew-edit-${c.id}`}
                              className="p-1.5 border border-slate-200 rounded hover:bg-slate-50">
                        <Pencil size={12}/>
                      </button>
                      <button onClick={() => remove(kind, c)}
                              data-testid={`crew-delete-${c.id}`}
                              className="p-1.5 border border-red-200 text-red-600 rounded hover:bg-red-50">
                        <Trash size={12}/>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Desktop table */}
        <div className="hidden lg:block overflow-auto scroll-thin">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-xs text-slate-500">
                {(tab === "drivers"
                  ? "Employee ID,Name,License,License Expiry,Depot,Phone,Duty,Violations,Shift,Actions"
                  : "Employee ID,Name,Depot,Phone,Duty,Shift,Actions").split(",").map(h => (
                    <th key={h} className="px-3 py-2 font-medium">{h}</th>
                  ))}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={10} className="px-3 py-8 text-center text-slate-400">Loading…</td></tr>}
              {!loading && filtered.length === 0 && <tr><td colSpan={10} className="px-3 py-8"><EmptyState onAdd={() => setEdit({ kind: tab === "drivers" ? "driver" : "conductor", doc: null })} kind={tab}/></td></tr>}
              {filtered.map(c => {
                const kind = tab === "drivers" ? "driver" : "conductor";
                return (
                  <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`crew-row-${c.id}`}>
                    <td className="px-3 py-2 font-mono text-xs">{c.employee_id}</td>
                    <td className="px-3 py-2 font-semibold">{c.name}</td>
                    {tab === "drivers" && <>
                      <td className="px-3 py-2 font-mono text-xs">{c.license_number}</td>
                      <td className="px-3 py-2 text-xs">{c.license_expiry}</td>
                    </>}
                    <td className="px-3 py-2">{fmtDepot(c.depot, depotMap)}</td>
                    <td className="px-3 py-2 text-xs">
                      <a href={`tel:${c.phone}`} className="text-[#1E3A8A] hover:underline">{c.phone}</a>
                    </td>
                    <td className="px-3 py-2 tabular-nums">{c.duty_hours}h</td>
                    {tab === "drivers" && (
                      <td className="px-3 py-2">
                        <span className={`text-xs px-2 py-0.5 rounded ${c.violations > 0 ? "bg-red-50 text-red-700" : "bg-green-50 text-green-700"}`}>{c.violations}</span>
                      </td>
                    )}
                    <td className="px-3 py-2">
                      <button onClick={() => toggleShift(kind, c)}
                              className={`text-xs px-2 py-0.5 rounded border ${shiftBadge(c.shift_status)} cursor-pointer hover:opacity-80`}
                              title="Toggle shift">
                        {c.shift_status}
                      </button>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1">
                        <button onClick={() => setEdit({ kind, doc: c })}
                                data-testid={`crew-edit-${c.id}`}
                                className="p-1.5 border border-slate-200 rounded hover:bg-slate-50" title="Edit">
                          <Pencil size={12}/>
                        </button>
                        <button onClick={() => remove(kind, c)}
                                data-testid={`crew-delete-${c.id}`}
                                className="p-1.5 border border-red-200 text-red-600 rounded hover:bg-red-50" title="Delete">
                          <Trash size={12}/>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <CrewEditModal entry={edit} onClose={() => setEdit(null)} onSaved={load}/>
    </div>
  );
}

const EmptyState = ({ onAdd, kind }) => (
  <div className="p-8 text-center">
    {kind === "drivers" ? <UserCircle size={42} weight="duotone" className="text-slate-300 mx-auto mb-3"/> : <IdentificationCard size={42} weight="duotone" className="text-slate-300 mx-auto mb-3"/>}
    <div className="font-display font-bold text-slate-700">No {kind} match your filters</div>
    <div className="text-xs text-slate-500 mt-1 mb-3">Try clearing filters or add a new {kind === "drivers" ? "driver" : "conductor"}.</div>
    <button onClick={onAdd}
            className="ktcl-btn-primary px-4 py-2 rounded-md text-sm font-semibold inline-flex items-center gap-1.5">
      <Plus size={14}/>Add {kind === "drivers" ? "Driver" : "Conductor"}
    </button>
  </div>
);

/* ===========================  Add / Edit modal  =========================== */
function CrewEditModal({ entry, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!entry) return;
    if (entry.doc) {
      setForm(entry.doc);
    } else {
      // New
      setForm({
        employee_id: "",
        name: "",
        depot: "Panaji",
        phone: "",
        duty_hours: 0,
        shift_status: "Off Duty",
        ...(entry.kind === "driver" ? {
          license_number: "",
          license_expiry: "",
          violations: 0,
        } : {}),
      });
    }
  }, [entry]);

  if (!entry) return null;

  const isDriver = entry.kind === "driver";
  const isEditing = !!entry.doc;

  const submit = async () => {
    if (!form.employee_id?.trim()) { toast.error("Employee ID is required"); return; }
    if (!form.name?.trim()) { toast.error("Name is required"); return; }
    if (!form.depot) { toast.error("Pick a depot"); return; }
    if (!form.phone?.trim()) { toast.error("Phone is required"); return; }
    if (isDriver) {
      if (!form.license_number?.trim()) { toast.error("License number is required"); return; }
      if (!form.license_expiry) { toast.error("License expiry is required"); return; }
    }
    setBusy(true);
    try {
      if (isEditing) {
        await api.patch(`/crew/${entry.kind}s/${entry.doc.id}`, form);
        toast.success(`${form.name} updated`);
      } else {
        await api.post(`/crew/${entry.kind}s`, form);
        toast.success(`${form.name} added`);
      }
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[1300] flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div data-testid="crew-edit-modal"
           className="bg-white w-full max-w-lg max-h-[90dvh] sm:max-h-[92vh] overflow-auto rounded-t-2xl sm:rounded-lg"
           onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white p-4 sm:p-5 border-b border-slate-200 flex items-start justify-between z-10 rounded-t-2xl sm:rounded-t-lg relative">
          <div className="absolute top-1.5 left-1/2 -translate-x-1/2 w-10 h-1 bg-slate-300 rounded-full sm:hidden"/>
          <div className="mt-1 sm:mt-0">
            <div className="flex items-center gap-2">
              {isDriver ? <UserCircle size={18} className="text-[#1E3A8A]"/> : <IdentificationCard size={18} className="text-[#1E3A8A]"/>}
              <h3 className="font-display text-lg sm:text-xl font-bold">
                {isEditing ? "Edit" : "Add"} {isDriver ? "Driver" : "Conductor"}
              </h3>
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              {isEditing ? `${form.employee_id || ""} · ${form.name || ""}` : "Required fields are marked with *"}
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 p-1 mt-1 sm:mt-0 shrink-0">
            <X size={20}/>
          </button>
        </div>

        <div className="p-4 sm:p-5 space-y-3">
          <Field label="Employee ID *">
            <input data-testid="crew-employee-id" value={form.employee_id || ""}
                   onChange={e => setForm({ ...form, employee_id: e.target.value.toUpperCase() })}
                   placeholder={isDriver ? "DRV200" : "CON300"} disabled={isEditing}
                   className={`w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm outline-none font-mono ${isEditing ? "opacity-60 cursor-not-allowed" : "focus:border-[#1E3A8A]"}`}/>
          </Field>

          <Field label="Full Name *">
            <input data-testid="crew-name" value={form.name || ""}
                   onChange={e => setForm({ ...form, name: e.target.value })}
                   placeholder="e.g. Ravi Naik"
                   className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm outline-none focus:border-[#1E3A8A]"/>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Depot *" icon={Buildings}>
              <select data-testid="crew-depot" value={form.depot || "Panaji"}
                      onChange={e => setForm({ ...form, depot: e.target.value })}
                      className="w-full pl-8 pr-2 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm outline-none">
                {DEPOTS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </Field>
            <Field label="Phone *" icon={Phone}>
              <input data-testid="crew-phone" type="tel" value={form.phone || ""}
                     onChange={e => setForm({ ...form, phone: e.target.value })}
                     placeholder="+919999988888"
                     className="w-full pl-8 pr-2 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm outline-none font-mono focus:border-[#1E3A8A]"/>
            </Field>
          </div>

          {isDriver && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="License Number *">
                <input data-testid="crew-license" value={form.license_number || ""}
                       onChange={e => setForm({ ...form, license_number: e.target.value.toUpperCase() })}
                       placeholder="GA-12345678"
                       className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm outline-none font-mono focus:border-[#1E3A8A]"/>
              </Field>
              <Field label="License Expiry *" icon={CalendarBlank}>
                <input data-testid="crew-license-expiry" type="date" value={form.license_expiry || ""}
                       onChange={e => setForm({ ...form, license_expiry: e.target.value })}
                       className="w-full pl-8 pr-2 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm outline-none focus:border-[#1E3A8A]"/>
              </Field>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Field label="Shift Status">
              <select data-testid="crew-shift" value={form.shift_status || "Off Duty"}
                      onChange={e => setForm({ ...form, shift_status: e.target.value })}
                      className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm outline-none">
                {SHIFT_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Duty Hours (today)">
              <input data-testid="crew-duty-hours" type="number" step="0.5" min="0" max="24"
                     value={form.duty_hours ?? 0}
                     onChange={e => setForm({ ...form, duty_hours: Number(e.target.value) })}
                     className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm outline-none tabular-nums focus:border-[#1E3A8A]"/>
            </Field>
          </div>

          {isDriver && (
            <Field label="Violations">
              <input data-testid="crew-violations" type="number" min="0"
                     value={form.violations ?? 0}
                     onChange={e => setForm({ ...form, violations: Number(e.target.value) })}
                     className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm outline-none tabular-nums focus:border-[#1E3A8A]"/>
            </Field>
          )}
        </div>

        <div className="sticky bottom-0 bg-white border-t border-slate-200 p-4 flex justify-end gap-2">
          <button onClick={onClose}
                  className="px-4 py-2 border border-slate-200 rounded-md text-sm hover:bg-slate-50">
            Cancel
          </button>
          <button data-testid="crew-save" disabled={busy} onClick={submit}
                  className="ktcl-btn-primary px-5 py-2 rounded-md text-sm font-semibold disabled:opacity-60 inline-flex items-center gap-1.5">
            <CheckCircle size={14} weight="fill"/>
            {busy ? "Saving…" : (isEditing ? "Update" : "Add")}
          </button>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, icon: Icon, children }) => (
  <div>
    <div className="text-[10px] uppercase text-slate-500 font-semibold tracking-wide mb-1">{label}</div>
    <div className="relative">
      {Icon && <Icon size={14} className="absolute top-1/2 -translate-y-1/2 left-2.5 text-slate-400 pointer-events-none"/>}
      {children}
    </div>
  </div>
);
